/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {productUrlBuilder, rebuildPathWithParams} from '@salesforce/retail-react-app/app/utils/url'
import {
    AGEC_ELIGIBLE_LIGNE_PROD,
    MISTRAL_PAGE_ID,
    MISTRAL_PAGE_PATH,
    FAMILY_CATEGORY_PREFIX
} from '../constants'
import {COUNTRY_TRANSLATIONS} from '../components/forms/country-list'
import iso3ToIso2Countries from '../country-iso3.json'
import {FormattedMessage} from 'react-intl'

/**
 * Generates a display-friendly object of variation attribute values with proper formatting.
 *
 * This function processes a set of variation attributes and their corresponding values,
 * returning an object where each key is the attribute's name and each value is the
 * display-friendly version of the attribute's value. Special formatting is applied to
 * certain attributes, such as capitalizing the first letter of color names.
 *
 * @param {Array<Object>} variationAttributes - An array of variation attribute objects, each containing an 'id' and 'values'.
 * @param {Object} [values={}] - An optional object mapping attribute IDs to their selected values.
 * @returns {Object} An object where keys are attribute names and values are the formatted display names of the attributes.
 *
 * @example
 * const displayValues = getDisplayVariationValues(
 *     [ { "id": "color", "name": "Colour", "values": [ { "name": "royal", "orderable": true, "value": "JJ5FUXX" } ] } ],
 *     { "color": "JJ5FUXX" }
 * )
 * // returns { "Colour": "Royal" }
 */
export const getDisplayVariationValues = (variationAttributes, values = {}) => {
    const returnVal = Object.entries(values).reduce((acc, [id, value]) => {
        const attribute = variationAttributes.find(({id: attributeId}) => attributeId === id)
        const attributeValue = attribute.values.find(
            ({value: attributeValue}) => attributeValue === value
        )
        let displayName = attributeValue.name
        if (attribute.name === 'Colour') {
            displayName =
                attributeValue.name.charAt(0).toUpperCase() +
                attributeValue.name.slice(1).toLowerCase()
        }
        return {
            ...acc,
            [attribute.name]: displayName
        }
    }, {})
    return returnVal
}

/**
 * Normalizes data for product sets and product bundles into the same format
 * Useful for operations that apply to both product sets and product bundles
 *
 * @param {Object} product - A product set or product bundle
 * @returns {Object} - returns normalized product if product is a set/bundle, otherwise returns original product
 */
export const normalizeSetBundleProduct = (product) => {
    if (!product?.type.set && !product?.type.bundle) return product
    return {
        ...product,
        childProducts: product?.type.set
            ? product.setProducts.map((child) => {
                  return {product: child, quantity: null}
              })
            : product.bundledProducts
    }
}

/**
 * Creates an array of product items used to update the child variant selections for a product bundle,
 * usually in an updateItemToBasket/updateItemsToBasket shopper baskets API call
 *
 * @param {Object} bundle - A product that is a bundle.
 * @param {Array} childProductSelections - An array of the bundle child products with a selected variant.
 *      Each item in the array should have the product, variant, and quantity.
 * @returns {Array} - an array of bundle child product items that have the updated variant
 */
export const getUpdateBundleChildArray = (bundle, childProductSelections) => {
    const itemsToBeUpdated = []
    bundle?.bundledProductItems?.forEach((bundleChild) => {
        const childSelection = childProductSelections?.find(
            (childProduct) =>
                childProduct.product?.id === bundleChild.productId ||
                childProduct.product?.itemId === bundleChild.itemId
        )

        // only update the item if the selected variant is different then what's in the current bundle
        if (childSelection && childSelection.variant?.productId !== bundleChild.productId) {
            itemsToBeUpdated.push({
                itemId: bundleChild.itemId,
                productId: childSelection.variant.productId,
                quantity: childSelection.quantity ?? bundleChild.quantity
            })
        }
    })
    return itemsToBeUpdated
}

/**
 * This function extract the price information of a given product
 * If a product is a master (isMaster) or variant group (isVariationGroup),
 *  currentPrice: get the lowest price (including promotional prices) among variants
 *  listPrice: get the list price of the variant that has lowest price (including promotional price)
 *  maxPrice: the max price in tieredPrices of variant that has lowest price
 * @param {object} product - product detail object
 * @param {object} opts - options to pass into the function like intl, quantity, and currency
 */
export const getPriceData = (product, opts = {}) => {
    const {quantity = 1} = opts
    const isASet = product?.hitType === 'set' || !!product?.type?.set
    const isMaster = product?.hitType === 'master' || !!product?.type?.master
    const isVariationGroup =
        product?.hitType === 'variation_group' || !!product?.type?.variationGroup

    // grab the variant that has the lowest price (including promotional price and promotional callout message)
    const variantWithLowestPrice = findLowestPrice(product)
    const currentPrice = variantWithLowestPrice?.minPrice
    const isOnSale = !!variantWithLowestPrice?.promotion?.promotionalPrice
    const beforePromoPrice = (isOnSale && variantWithLowestPrice?.data?.price) || null
    const promoCalloutMsg = (isOnSale && variantWithLowestPrice?.promotion?.calloutMsg) || null
    const promotionalPricePromoIds = variantWithLowestPrice?.promotionalPricePromoIds || []

    // since the price is the lowest value among price books, each product will have at lease a single item tiered price at quantity 1
    // the highest value of tieredPrices is presumptively the list price
    const tieredPrices = variantWithLowestPrice?.data?.tieredPrices || product?.tieredPrices || []
    const maxTieredPrice = tieredPrices?.length
        ? Math.max(...tieredPrices.map((item) => item.price))
        : undefined
    const highestTieredPrice = tieredPrices.find((tier) => tier.price === maxTieredPrice)
    const listPrice = highestTieredPrice?.price || null

    // if a product has tieredPrices, get the tiered that has the higher closest quantity to current quantity
    const filteredTiered = tieredPrices.filter((tiered) => tiered.quantity <= quantity)
    const closestTieredPrice =
        filteredTiered.length &&
        filteredTiered.reduce((prev, curr) => {
            return Math.abs(curr.quantity - quantity) < Math.abs(prev.quantity - quantity)
                ? curr
                : prev
        })

    return {
        currentPrice,
        beforePromoPrice,
        promoCalloutMsg,
        promotionalPricePromoIds,
        listPrice,
        pricePerUnit: product?.pricePerUnit,
        isOnSale: isOnSale,
        isASet,
        isMaster,
        isVariationGroup,
        // For a product, set price is the lowest price of its children, so the price should be considered a range
        // For a master product, when it has more than 2 variants, we use the lowest priced variant, so it is  considered a range price
        //      but for master that has one variant, it is not considered range
        isRange: (isMaster && product?.variants?.length > 1) || isASet || false,
        // priceMax is for product set
        tieredPrice: closestTieredPrice?.price,
        maxPrice: product?.priceMax || maxTieredPrice
    }
}

/**
 * Filters the provided product `ImageGroups` array with the optional filters.
 *
 * @param {ImageGroup[]} imageGroups
 * @param {object} filters
 * @param {string} filters.viewType
 * @param {object} filters.variationValues
 *
 * @returns {ImageGroup[]} filteredImageGroups
 */
export const filterImageGroups = (imageGroups = [], filters) => {
    if (!filters) {
        throw new Error(`Missing required "filters" argument`)
    }

    const {viewType, variationValues = {}} = filters
    // NOTE: For future feature enhancement compatibility we use an object for the `variationValues` as
    // we might want to filter on more than the one attribute id/value pair.
    const [selectedAttributeId, selectedAttributeValue] = Object.entries(variationValues)[0] || []

    // Filters
    const typeFilter = viewType ? (group) => group.viewType === viewType : () => true
    const attributeFilter =
        !!selectedAttributeId && !!selectedAttributeValue
            ? ({variationAttributes = []}) =>
                  variationAttributes.some(
                      ({id, values}) =>
                          id === selectedAttributeId &&
                          !!values.find(({value}) => value === selectedAttributeValue)
                  )
            : () => true

    return imageGroups?.filter(typeFilter)?.filter(attributeFilter)
}

/**
 * Provided a product this function will return the variation attibutes decorated with
 * `href` and `swatch` image for the given attribute values. This allows easier access
 * when creating components that commonly use this information.
 *
 * @param {Product} product
 * @param {object} opts
 * @param {string} opts.swatchViewType - The `viewTypeId` for the swatch image. Defaults to `swatch`.
 *
 * @returns {VariationAttributes[]} decoratedVariationAttributes
 */
export const getDecoratedVariationAttributes = (product, opts = {}) => {
    const swatchViewType = opts.swatchViewType || 'swatch'

    return product?.variationAttributes?.map((variationAttribute) => ({
        ...variationAttribute,
        values: variationAttribute.values.map((value) => {
            const variationValues = {[variationAttribute.id]: value.value}

            return {
                ...value,
                swatch: filterImageGroups(product.imageGroups, {
                    viewType: swatchViewType,
                    variationValues
                })?.[0]?.images[0],
                href: rebuildPathWithParams(
                    productUrlBuilder({id: product.productId}),
                    variationValues
                )
            }
        })
    }))
}

/**
 * Find the lowest price of a product, across all of its price books and promotions
 * @param {Object} product - product data from the API
 * @returns {Object|undefined} the lowest price for the given product. If it's a promotional price, the promotion will also be returned.
 */
export const findLowestPrice = (product) => {
    if (!product) return

    // Look at all of the variants, only if it's a master product OR variation group.
    // i.e. when a shopper has narrowed down to a variant, do not look into other variants
    const isMaster = product.hitType === 'master' || !!product.type?.master
    const isVariationGroup =
        product?.hitType === 'variation_group' || !!product.type?.variationGroup
    const isVariations = isMaster || isVariationGroup

    if (isVariations && !product.variants) {
        console.warn(
            'Expecting `product.variants` to exist. For more accuracy, please tweak your API request to ask for the variants details.'
        )
    }
    const array = isVariations && product.variants ? product.variants : [product]

    const res = array.reduce(
        (prev, data) => {
            // PLP useProductSearch hook - productSearch API: array => product.variants - data.productPromotions
            // PDP useProduct hook - getProduct API:
            //  case 1 - product variant: array => [product] with data.productPromotions
            //  case 2 - variation group: array => product.variants with data.c_productPromotions
            const promotions = data?.productPromotions || data?.c_productPromotions || []
            const [smallestPromotionalPrice, promo] = getSmallestValByProperty(
                promotions,
                'promotionalPrice'
            )

            let salePrice = data.price
            let promotion = null
            if (smallestPromotionalPrice && smallestPromotionalPrice < data.price) {
                salePrice = smallestPromotionalPrice
                promotion = promo
            }

            // Collect promotion IDs where promotionalPrice is not null
            const currentPromotionalPricePromoIds = promotions
                .filter((promo) => promo.promotionalPrice != null)
                .map((promo) => promo.promotionId)

            // Use a Set to ensure unique promotion IDs
            const promotionalPricePromoIds = Array.from(
                new Set([...prev.promotionalPricePromoIds, ...currentPromotionalPricePromoIds])
            )

            return salePrice < prev.minPrice
                ? {minPrice: salePrice, promotion, data, promotionalPricePromoIds}
                : {...prev, promotionalPricePromoIds}
        },
        {minPrice: Infinity, promotion: null, data: null, promotionalPricePromoIds: []}
    )
    return {
        ...res,
        // when minPrice is infinity, meaning there is no variant with lowest price found, we rest the value to 0 as minPrice to be able to use this value on UI
        minPrice: res.minPrice === Infinity ? 0 : res.minPrice
    }
}

/**
 * @private
 * Find the smallest value by key from a given array
 * @returns {Array} an array of such smallest value and the item containing this value
 * @example
 * const [value, itemContainingValue] = getSmallestValByProperty(array, key)
 */
const getSmallestValByProperty = (arr, key) => {
    if (!arr || !arr.length) return []
    if (!key) {
        throw new Error('Please specify a key.')
    }
    const filtered = arr.filter((item) => {
        const value = item[key]
        return value === 0 || value === '0' ? true : Number(value)
    })
    if (filtered.length === 0) return []

    return filtered.reduce(
        (prev, item) => {
            const value = item[key]
            const [prevValue] = prev
            return value < prevValue ? [value, item] : prev
        },
        [Infinity, null]
    )
}

/**
 * Provided a product this function will return the variation attibutes decorated with
 * `href` and `swatch` image for the given attribute values. This allows easier access
 * when creating components that commonly use this information.
 *
 * @param {Product} product
 * @param {object} opts
 * @param {string} opts.swatchViewType - The `viewTypeId` for the swatch image. Defaults to `swatch`.
 *
 * @returns {Array<Object>} An array of objects, each representing a group of variation values.
 *
 * @example
 * Example output:
 *  [
 *      {
 *          "id": "color",
 *          "values": [
 *              {
 *                  "value": "390",
 *                  "href": "/product/MOOP701P-390?color=390"
 *              },
 *              {
 *                  "value": "010",
 *                  "href": "/product/MOOP701P-010?color=010"
 *              },
 *              {
 *                  "value": "990",
 *                  "href": "/product/MOOP701P-990?color=990"
 *              }
 *          ]
 *      }
 *  ]
 */
export const groupVariationValues = (product, opts = {}) => {
    const swatchViewType = opts.swatchViewType || 'swatch'

    // Initialize a map to group by variationValues key
    const groupedAttributes = new Map()

    product?.variationGroups?.forEach((variationGroup) => {
        const {productId, variationValues = {}} = variationGroup

        Object.entries(variationValues).forEach(([key, value]) => {
            const variationValuesObj = {[key]: value}

            // Get swatch image
            const swatch = filterImageGroups(product.imageGroups, {
                viewType: swatchViewType,
                variationValues: variationValuesObj
            })?.[0]?.images[0]

            // Build href link
            const href = rebuildPathWithParams(
                productUrlBuilder({id: productId}),
                variationValuesObj
            )

            // Find the color attribute from c_variationAttributes
            const colorAttribute =
                product?.c_variationAttributes?.find((attr) => attr.value === value) || {}
            const colorLabel = colorAttribute?.c_colorLabel || ''
            const colorRegroupment = colorAttribute?.c_colorRegroupment || ''

            // Create the value object
            const valueObject = {
                value,
                colorLabel,
                colorRegroupment,
                swatch: swatch && {
                    alt: swatch.alt,
                    link: swatch.link,
                    title: swatch.title
                },
                href
            }

            // Add to the grouped attributes map
            if (!groupedAttributes.has(key)) {
                groupedAttributes.set(key, [])
            }
            groupedAttributes.get(key).push(valueObject)
        })
    })

    // Convert the map to an array of objects
    return Array.from(groupedAttributes.entries()).map(([id, values]) => ({
        id,
        values
    }))
}

/**
 * Determines if a given product is eligible for AGEC (Anti-Gaspillage et Économie Circulaire).
 *
 * This function checks if the product has a specific line code and verifies its presence
 * in the list of AGEC eligible product line codes.
 *
 * @param {Object} product - The product object to be evaluated.
 * @param {string} product.c_orliweb_code_ligne_produit - The line code of the product.
 * @returns {boolean} - Returns true if the product is AGEC eligible, otherwise false.
 */
export const isAGECProduct = (product) => {
    return product && product.c_orliweb_code_ligne_produit
        ? AGEC_ELIGIBLE_LIGNE_PROD.includes(product.c_orliweb_code_ligne_produit)
        : false
}

/**
 * Determines the type of a given product.
 *
 * @param {Object} product - The product object to evaluate, which should contain type information.
 * @returns {Object} An object containing booleans.
 */
export const getProductType = (product) => {
    return {
        isSet: product?.type?.set ?? false,
        isBundle: product?.type?.bundle ?? false
    }
}

/**
 * Retrieves the localized country name using the ISO3 code.
 *
 * This function converts an ISO3 country code to an ISO2 code using a JSON mapping,
 * then retrieves the corresponding localized country name from the COUNTRY_TRANSLATIONS.
 * If the country name is not found, it returns a default message indicating that the
 * information is being revised.
 *
 * @param {string} iso3Code - The ISO3 country code.
 * @returns {JSX.Element|string} - The localized country name or a default message if not found.
 */

export const getCountryName = (iso3Code) => {
    // Find the ISO2 code corresponding to the ISO3 code
    const iso2Code = Object.keys(iso3ToIso2Countries).find(
        (key) => iso3ToIso2Countries[key] === iso3Code
    )
    const message = iso2Code ? COUNTRY_TRANSLATIONS[iso2Code] : null

    return message ? (
        <FormattedMessage {...message} />
    ) : (
        <FormattedMessage
            defaultMessage="being revised"
            id="product_agec.pdp.short_text_being_revised"
        />
    )
}

/**
 * Determines if all country traceability fields are empty.
 *
 * This function checks if the provided traceability fields (madeIn, origineMatiere, transformation)
 * are all empty, undefined, or null. It uses the `areFieldsEmpty` utility function to perform this check.
 *
 * @param {string} madeIn - The country code where the product is made.
 * @param {string} origineMatiere - The country code of the material origin.
 * @param {string} transformation - The country code where the product is transformed.
 * @returns {boolean} - Returns true if all traceability fields are empty, otherwise false.
 */
export const areTraceabilityFieldsEmpty = (madeIn, origineMatiere, transformation) => {
    return areFieldsEmpty([madeIn, origineMatiere, transformation])
}
/**
 * Determines if all fields in an array are empty.
 *
 * This utility function checks if each field in the provided array is either undefined, null, or an empty string.
 * It returns true if all fields meet these criteria, indicating that they are empty.
 *
 * @param {Array} fields - An array of fields to check for emptiness.
 * @returns {boolean} - Returns true if all fields are empty, otherwise false.
 */
export const areFieldsEmpty = (fields) => {
    return fields.every((field) => field === undefined || field === null || field === '')
}

/**
 * Retrieves SEO product image from the provided variationGroup product object.
 *
 * This function extracts the first image link from each image group for the give variationGroup only,
 * but only the viewType=front one within the given SEO product.
 * If the `disBaseLink` is available, it is used; otherwise,
 * the `link` is returned. If the `seoProduct` or its `imageGroups` are not
 * defined, an empty array is returned.
 *
 * @param {Object} seoProduct - The SEO product object containing image groups.
 * @param {Array} seoProduct.imageGroups - An array of image group objects, each containing images.
 * @param {Object} variationGroup - The variationGroup object to use to filter the images
 * @returns {Array<string>} An array of image URLs extracted from the SEO product.
 */
export const getSeoProductImages = (seoProduct, variationGroup) => {
    if (!seoProduct || !seoProduct?.imageGroups) {
        return ''
    }

    // Extract the target attributes to match (e.g., { color: "806" })
    const targetValues = variationGroup?.variationValues

    const matchingGroup = seoProduct.imageGroups.find((item) => {
        // 1. Must be a "front" view
        if (item.viewType !== 'front') {
            return false
        }

        // 2. If a variationGroup is provided, the image group must match its attributes
        if (targetValues && item.variationAttributes) {
            const matchesVariation = Object.entries(targetValues).every(([key, val]) => {
                // Find the matching attribute in the image group (e.g., id: "color")
                const attribute = item.variationAttributes.find((attr) => attr.id === key)

                // Check if the value exists in the image group's values array
                return attribute?.values.some((v) => String(v.value) === String(val))
            })

            if (!matchesVariation) {
                return false
            }
        }

        return true
    })

    // If we found a match, return the string URL. Otherwise, return an empty string.
    if (matchingGroup) {
        const image = matchingGroup.images?.[0]
        return image?.disBaseLink || image?.link || ''
    }

    return ''
}

/**
 * Generates HTML content for product details using provided descriptions.
 *
 * This function concatenates the `productDetails`, and `longDescription`
 * into a single HTML string. If none of these are provided, it defaults to a specified message.
 *
 * @param {Object} params - The parameters for generating the HTML content.
 * @param {string} [params.productDetails] - The detailed description of the product.
 * @param {string} [params.longDescription] - A long description of the product.
 * @param {string} params.defaultMessage - The default message to display if no descriptions are provided.
 * @returns {string} The generated HTML content as a string.
 *
 * @example
 * const htmlContent = generateProductDetailsHTML({
 *     productDetails: "<p>Product details here</p>",
 *     longDescription: "<p>Long description here</p>",
 *     defaultMessage: "No details available"
 * });
 * console.log(htmlContent);
 */
export const generateProductDetailsHTML = ({productDetails, longDescription}) => {
    return `
        ${productDetails || ''}
        ${longDescription || ''}
    `
}

/**
 * Generates an automated link for a given product based on its attributes.
 *
 * This function evaluates the properties of a product to determine the appropriate
 * link to be returned. It checks for specific conditions related to the product's
 * model and family status to construct the link accordingly.
 *
 * @param {Object} product - The product object for which the link is to be created.
 * @param {string} product.c_tag - A tag associated with the product, required for link creation.
 * @param {string} [product.c_orliweb_product_model] - The product model identifier, used to determine specific page links.
 * @param {string} [product.c_orliweb_isFamily] - Indicates the family status of the product, which influences the link structure.
 * @param {string} [product.c_orliweb_stat20_mat_rgt] - A material right identifier used for family category links.
 * @param {string} [product.c_tagLink] - A custom link associated with the product, used if no other conditions are met.
 * @returns {string|null} The constructed link for the product or null if the product or required tag is not provided.
 */
export const createAutomatedLinkForTag = (product) => {
    let tagWrapperLink = null
    if (!product?.c_tag) return null

    if (product?.c_orliweb_product_model === MISTRAL_PAGE_ID) {
        tagWrapperLink = MISTRAL_PAGE_PATH
    } else if (
        product?.c_orliweb_isFamily === 'F' ||
        product?.c_orliweb_isFamily === 'MD' ||
        product?.c_orliweb_isFamily === 'FS'
    ) {
        tagWrapperLink = `/category/${FAMILY_CATEGORY_PREFIX}${product.c_orliweb_stat20_mat_rgt}`
    } else if (
        product?.c_orliweb_isFamily === 'No' &&
        product?.c_orliweb_product_model !== MISTRAL_PAGE_ID &&
        product?.c_tagLink
    ) {
        tagWrapperLink = product.c_tagLink
    }

    return tagWrapperLink
}

/**
 * Retrieves the variation group object from a variant that matches the variant's color value.
 * @param {Object} variant - The product variant object, expected to contain variationGroups and c_color properties.
 * @returns {Object|null} The matching variation group object if found; otherwise, null.
 */
export const getVariationGroupOfVariant = (variant) => {
    const variationGroups = variant?.variationGroups || []

    return (
        variationGroups?.find(
            (variationGroup) => variationGroup.variationValues?.color === variant?.c_color
        ) || null
    )
}
